import React, { memo, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import "./CalendarView.css";
import { useNavigate } from "react-router-dom";
import { addDay, datesInRange, dateToString, getMonth, getWeekDayIndex, isSameDay, normalize } from "../dates";
import { plannedMeals, PlannedMeal, plannedMealUrl } from "../data";
import { RecipeServingTuple, ShopMeals } from "../components/ShoppingListView";
import { Box, Button, ButtonGroup, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useCurrentHouseholdId } from "../contexts/AuthProvider";
import { Helmet } from "react-helmet";
import { flushSync } from "react-dom";
import TiledRecipeBackground from "../components/TiledRecipeBackground";
import RecipeMenuItem from "../components/base/RecipeMenuItem";

function RecipesTooltip({ date, meals }: { date: Date; meals: PlannedMeal[] }) {
    return (
        <Stack direction="column">
            <Typography variant="h6">
                {date.getDate()} {getMonth(date)}
            </Typography>
            {meals.map((meal, index) => {
                const recipe = meal.recipe;
                return (
                    <RecipeMenuItem
                        key={`${meal.id}_${index}`}
                        url={plannedMealUrl(meal)}
                        name={recipe.name}
                        imageId={recipe.image_id}
                    ></RecipeMenuItem>
                );
            })}
        </Stack>
    );
}

interface DayButtonProps {
    date: Date;
    recipes?: PlannedMeal[];
    isSelected: boolean;
    onStartSelect: (date: Date) => void;
    onEndSelect: (date: Date, isUp: boolean) => void;
    divRef?: React.LegacyRef<any>;
}

function DayButton({ date, recipes, isSelected, onStartSelect, onEndSelect, divRef }: DayButtonProps) {
    const dayNum = date.getDate();
    const isEvenMonth = date.getMonth() % 2 === 0;
    const isToday = isSameDay(new Date(), date);
    const wrapperClasses = "day-button-wrapper" + (isSelected ? " selected" : "");
    const dayTextClasses = "day-button-day" + (isToday ? " today" : "");

    const onPointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isPressed(e)) return;

        onEndSelect(date, false);
    };

    const weekDay = getWeekDayIndex(date);
    let borderClass: string = "";
    if (dayNum === 1 && weekDay !== 0) {
        borderClass = " day-next-month-top-left";
    } else if (dayNum <= 7) {
        borderClass = " day-next-month-top";
    }

    return (
        <div className={"day-button-month" + (isEvenMonth ? " next-month" : "") + borderClass} ref={divRef}>
            <div
                onPointerDown={e => {
                    e.preventDefault();
                    onStartSelect(date);
                }}
                onPointerEnter={onPointerEnter}
                onPointerUp={_ => onEndSelect(date, true)}
                className={wrapperClasses}
            >
                {recipes?.length ? (
                    <>
                        <TiledRecipeBackground key="1" recipes={recipes.map(p => p.recipe)} />
                        <div key="2" className="day-button-bg-cover"></div>
                        <Tooltip
                            key="3"
                            title={recipes?.length ? <RecipesTooltip date={date} meals={recipes} /> : undefined}
                            placement="right"
                            arrow
                        >
                            <InfoIcon
                                sx={{
                                    color: "white",
                                    position: "absolute",
                                    bottom: 0,
                                    right: 0,
                                    bgcolor: "rgba(0, 0, 0, 0.47)",
                                    borderRadius: 9999,
                                }}
                            ></InfoIcon>
                        </Tooltip>
                    </>
                ) : (
                    <></>
                )}

                <div className={dayTextClasses}>{dayNum}</div>
            </div>
        </div>
    );
}

interface CompactDayButtonProps {
    date: Date;
    recipes?: PlannedMeal[];
    isSelected: boolean;
    onSelect?: (day: Date) => void;
    divRef?: React.LegacyRef<any>;
}

function CompactDay({ date, recipes, isSelected, onSelect, divRef }: CompactDayButtonProps) {
    const dayNum = date.getDate();
    const isToday = isSameDay(new Date(), date);

    return (
        <Stack
            direction="column"
            minHeight="calc(100vh/6)"
            border="1px solid darkgray"
            overflow="hidden"
            spacing="2px"
            bgcolor={isSelected ? "rgb(47, 199, 249)" : undefined}
            onClick={onSelect ? () => onSelect(date) : undefined}
        >
            {divRef ? <div ref={divRef}></div> : <></>}
            <Typography
                textAlign="center"
                variant="body2"
                sx={{
                    bgcolor: isToday ? "rgb(99, 141, 214)" : undefined,
                    borderRadius: 9999,
                    userSelect: "none",
                }}
            >
                {dayNum}
            </Typography>
            {recipes?.map((meal, index) => {
                return (
                    <Typography
                        variant="caption"
                        sx={{
                            textWrap: "nowrap",
                            textOverflow: "ellipsis",
                            bgcolor: "rgb(236, 187, 255)",
                            borderTopLeftRadius: 9999,
                            borderBottomLeftRadius: 9999,
                            paddingLeft: "5px",
                            overflow: "hidden",
                        }}
                        key={`${meal.recipe.id}_${index}`}
                    >
                        {meal.recipe.name}
                    </Typography>
                );
            })}
        </Stack>
    );
}

const MemoizedDayButton = memo(DayButton);
const MemoizedCompactDay = memo(CompactDay);

interface CompactCalendarProps {
    days: Date[];
    focusDay: Date;
    dateMealMap: Map<string, PlannedMeal[]>;
    firstDate?: Date;
    lastDate?: Date;
    todayRef?: React.LegacyRef<any>;
    onSelect?: (day: Date) => void;
}

function CompactCalendar({
    days,
    dateMealMap,
    firstDate,
    lastDate,
    focusDay,
    todayRef,
    onSelect,
}: CompactCalendarProps) {
    let calendarGridItems: ReactElement[] = [];
    for (let week = 0; week * 7 < days.length; ++week) {
        const weekStartIndex = week * 7;
        for (let day = 0; day < 7; ++day) {
            const index = weekStartIndex + day;
            const d = days[index];
            let isSelected = false;
            if (firstDate !== undefined && lastDate !== undefined) {
                if (firstDate < lastDate) {
                    isSelected = firstDate <= d && d <= lastDate;
                } else {
                    isSelected = lastDate <= d && d <= firstDate;
                }
            }

            const isFocus = isSameDay(focusDay, d);

            calendarGridItems.push(
                <MemoizedCompactDay
                    key={index}
                    date={d}
                    recipes={dateMealMap.get(dateToString(d))}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    divRef={isFocus ? todayRef : undefined}
                />
            );
        }
    }

    return (
        <Box
            sx={{
                width: { md: "80%" },
                ml: "auto",
                mr: "auto",
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
            }}
        >
            {calendarGridItems}
        </Box>
    );
}

function isPressed(e: React.PointerEvent<HTMLDivElement>): boolean {
    return (e.buttons & 1) === 1;
}

function buildCalendar(focus: Date): Date[] {
    const dayInWeek = focus.getDay();
    const mondayThisWeek = addDay(focus, dayInWeek === 0 ? -6 : -(dayInWeek - 1));
    const days = new Array(8 * 7).fill(0).map((_, index) => normalize(addDay(mondayThisWeek, index - 3 * 7)));
    return days;
}

interface DateMealMap {
    range?: {
        first: Date;
        last: Date;
    };
    map: Map<string, PlannedMeal[]>;
}

async function loadPlannedMeals(
    currentHouseholdId: number,
    first: Date,
    last: Date,
    setDateMealMap: (value: React.SetStateAction<DateMealMap>) => void
): Promise<void> {
    const meals = await plannedMeals(currentHouseholdId, first, last);

    setDateMealMap(current => {
        const map = new Map(current.map);
        const datesToClear = datesInRange(first, last);
        for (const date of datesToClear) {
            map.delete(dateToString(date));
        }
        for (const meal of meals) {
            const dateKey = dateToString(new Date(meal.date));
            let recipes = map.get(dateKey);
            if (!recipes) {
                map.set(dateKey, (recipes = []));
            }
            recipes.push(meal);
        }
        return {
            map,
            range: current.range
                ? {
                      first: first < current.range.first ? first : current.range.first,
                      last: last > current.range.last ? last : current.range.last,
                  }
                : { first, last },
        };
    });
}

function CalendarView() {
    const navigate = useNavigate();
    const largeScreen = useMediaQuery("(min-width:900px)");
    const currentHouseholdId = useCurrentHouseholdId();
    const listRef = useRef<HTMLDivElement>(null);
    const todayRef = useRef<HTMLDivElement>(null);
    const [focusDay, _setFocusDay] = useState<Date>(new Date());
    const [days, setDays] = useState<Date[]>(() => buildCalendar(focusDay));
    const [dateMealMap, setDateMealMap] = useState<DateMealMap>({ map: new Map() });
    const isSelecting = useRef<boolean>(false);
    const [firstDate, setFirstDate] = useState<Date | undefined>(undefined);
    const [lastDate, setLastDate] = useState<Date | undefined>(undefined);

    const hasSelection = firstDate !== undefined && lastDate !== undefined;

    const setFocusDay = (day: Date) => {
        if (todayRef.current) {
            todayRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
        } else {
            flushSync(() => {
                _setFocusDay(day);
                setDays(buildCalendar(day));
                setFirstDate(undefined);
                setLastDate(undefined);
            });
            todayRef.current!?.scrollIntoView({ block: "center" });
        }
    };

    useEffect(() => {
        todayRef.current?.scrollIntoView({ block: "center" });
    }, []);

    const onLoadEarlier = () => {
        const firstMonday = days[0];
        const daysToAdd = 4 * 7;
        const moreDays = new Array(daysToAdd).fill(0).map((_, index) => addDay(firstMonday, index - daysToAdd));

        // TODO: Seems like this might not be needed when the right keys are set up in the list. Seems like React has
        // some handling for this.
        const oldHeight = listRef.current?.scrollHeight;
        const oldScrollPos = listRef.current?.scrollTop;
        flushSync(() => setDays(moreDays.concat(days)));
        if (oldHeight !== undefined && oldScrollPos !== undefined && listRef.current) {
            const diff = listRef.current.scrollHeight - oldHeight;
            listRef.current.scrollTop = oldScrollPos + diff;
        }
    };

    const onLoadLater = async () => {
        const lastSunday = days[days.length - 1];
        const daysToAdd = 4 * 7;
        const moreDays = new Array(daysToAdd).fill(0).map((_, index) => addDay(lastSunday, index + 1));
        setDays(days.concat(moreDays));
    };

    useEffect(() => {
        if (!currentHouseholdId) return;

        const range = dateMealMap.range;
        if (!range) {
            loadPlannedMeals(currentHouseholdId, days[0], days[days.length - 1], setDateMealMap);
            return;
        }

        const first = days[0];
        const last = days[days.length - 1];
        if (!isSameDay(first, range.first) && first < range.first) {
            loadPlannedMeals(currentHouseholdId, first, addDay(range.first, -1), setDateMealMap);
        }
        if (!isSameDay(last, range.last) && range.last < last) {
            loadPlannedMeals(currentHouseholdId, addDay(range.last, 1), last, setDateMealMap);
        }
    }, [currentHouseholdId, days, dateMealMap]);

    const onStartSelect = useCallback(
        (date: Date) => {
            setFirstDate(date);
            setLastDate(date);
            isSelecting.current = true;
        },
        [isSelecting]
    );

    const onEndSelect = useCallback((date: Date, isUp: boolean) => {
        if (!isSelecting.current) return;

        setLastDate(date);
        if (isUp) {
            isSelecting.current = false;
        }
    }, []);

    const onSelect = useCallback((date: Date) => {
        setFirstDate(date);
        setLastDate(date);
    }, []);

    const onPointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
        if (isSelecting && !isPressed(e)) {
            isSelecting.current = false;
        }
    };

    const onPlan = () => {
        const range = [firstDate!, lastDate!].sort((a, b) => (a === b ? 0 : a < b ? -1 : 1));
        const start = dateToString(range[0]);
        const end = dateToString(range[1]);

        navigate(`/plan?dates=${encodeURIComponent(`${start}_${end}`)}`);
    };

    const onShowShoppingList = async () => {
        const range = [firstDate!, lastDate!].sort((a, b) => (a === b ? 0 : a < b ? -1 : 1));
        let recipesToShop: ShopMeals = [];
        for (const date of datesInRange(range[0], range[1])) {
            const plannedMeals = dateMealMap.map.get(dateToString(date));
            if (plannedMeals) {
                recipesToShop.push(
                    ...plannedMeals.map(r =>
                        r.servings ? ([r.recipe.id, date.getTime(), r.servings] as RecipeServingTuple) : r.recipe.id
                    )
                );
            }
        }

        const meals = encodeURIComponent(JSON.stringify(recipesToShop));
        navigate(`/shopping-list?meals=${meals}`);
    };

    let calendarItem: ReactElement;
    if (largeScreen) {
        let calendarGridItems: ReactElement[] = [];
        for (let week = 0; week * 7 < days.length; ++week) {
            const weekStartIndex = week * 7;
            const weekHasMonthBreak = days[weekStartIndex].getDate() === 1 || days[weekStartIndex + 6].getDate() < 7;
            const weekKey = dateToString(days[weekStartIndex]);

            for (let day = 0; day < 7; ++day) {
                const index = weekStartIndex + day;
                const d = days[index];
                let isSelected = false;
                if (firstDate !== undefined && lastDate !== undefined) {
                    if (firstDate < lastDate) {
                        isSelected = firstDate <= d && d <= lastDate;
                    } else {
                        isSelected = lastDate <= d && d <= firstDate;
                    }
                }

                const isFocus = isSameDay(focusDay, d);
                const key = dateToString(d);
                calendarGridItems.push(
                    <MemoizedDayButton
                        key={key}
                        date={d}
                        recipes={dateMealMap.map.get(key)}
                        isSelected={isSelected}
                        onStartSelect={onStartSelect}
                        onEndSelect={onEndSelect}
                        divRef={isFocus ? todayRef : undefined}
                    />
                );
            }
            if (weekHasMonthBreak) {
                const sunday = days[weekStartIndex + 6];
                if (sunday.getMonth() === 0) {
                    calendarGridItems.push(
                        <Typography key={weekKey + "_monthY"} variant="h6" className="day-next-month-top">
                            {getMonth(sunday)} {sunday.getFullYear()}
                        </Typography>
                    );
                } else {
                    calendarGridItems.push(
                        <Typography key={weekKey + "_month"} variant="h6" className="day-next-month-top">
                            {getMonth(sunday)}
                        </Typography>
                    );
                }
            } else {
                calendarGridItems.push(<span key={weekKey + "_week"}></span>);
            }
        }

        calendarItem = (
            <Box
                sx={{
                    width: { md: "80%" },
                    ml: "auto",
                    mr: "auto",
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr [day]) 100px [month-end]",
                }}
            >
                {calendarGridItems}
            </Box>
        );
    } else {
        calendarItem = (
            <CompactCalendar
                days={days}
                dateMealMap={dateMealMap.map}
                focusDay={focusDay}
                firstDate={firstDate}
                lastDate={lastDate}
                todayRef={todayRef}
                onSelect={onSelect}
            ></CompactCalendar>
        );
    }

    return (
        <Box
            onPointerEnter={onPointerEnter}
            sx={{
                width: "100%",
                ml: "auto",
                mr: "auto",
                display: "flex",
                flexDirection: "column",
                minHeight: "100%",
                flexGrow: "1",
            }}
        >
            <Helmet>
                <title>Schema</title>
            </Helmet>
            <Stack
                direction="row"
                display="flex"
                margin="15px 15px 10px 15px"
                spacing="15px"
                sx={{
                    width: { md: "1000px" },
                    ml: "auto",
                    mr: "auto",
                }}
            >
                <ButtonGroup variant="contained">
                    <Button disabled={!hasSelection} onClick={onPlan}>
                        Planera
                    </Button>
                    <Button disabled={!hasSelection} onClick={onShowShoppingList}>
                        Inköpslista
                    </Button>
                </ButtonGroup>
                <div style={{ flexGrow: 1 }}></div>
                <Stack direction="row">
                    <Button onClick={() => setFocusDay(new Date())}>Idag</Button>
                </Stack>
            </Stack>
            <Box
                sx={{
                    position: "relative",
                    flexGrow: 1,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        left: 0,
                        right: 0,
                        overflowY: "scroll",
                    }}
                    ref={listRef}
                    onScroll={e => {
                        if (e.currentTarget.scrollTop <= 200) {
                            onLoadEarlier();
                        } else if (
                            e.currentTarget.scrollHeight - (e.currentTarget.scrollTop + e.currentTarget.clientHeight) <=
                            200
                        ) {
                            onLoadLater();
                        }
                    }}
                >
                    {calendarItem}
                </Box>
            </Box>
        </Box>
    );
}

export default CalendarView;
